import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ROLES = [
  { key: "customer", label: "Seeker" },
  { key: "astrologer", label: "Astrologer" },
  { key: "pandit", label: "Pandit" },
  { key: "vendor", label: "Vendor" },
];

export default function Register() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "customer", referral_code: ""
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await register(form);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome to Vedacosmos, ${res.user.name.split(" ")[0]}!`);
      nav("/dashboard");
    } else setErr(res.error);
  };

  return (
    <div className="min-h-[90vh] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute inset-0 starfield opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <Sparkles className="text-[#D4AF37]" size={20} strokeWidth={1.5} />
          <span className="font-serif text-2xl">Vedacosmos</span>
        </Link>

        <div className="bg-[#0A0C10] border border-white/10 rounded-2xl p-8 backdrop-blur-xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Begin</div>
          <h1 className="font-serif text-3xl mb-6">Create your sacred account</h1>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Full Name</label>
              <input
                data-testid="register-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Email</label>
              <input
                data-testid="register-email-input"
                type="email" required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Password</label>
              <input
                data-testid="register-password-input"
                type="password" required minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">I am a</label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {ROLES.map((r) => (
                  <button
                    type="button"
                    key={r.key}
                    data-testid={`register-role-${r.key}`}
                    onClick={() => setForm({ ...form, role: r.key })}
                    className={`py-2 text-xs rounded-md border transition-colors ${
                      form.role === r.key
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                        : "border-white/10 text-[#A0A4AB] hover:border-white/20"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Referral Code (optional)</label>
              <input
                data-testid="register-referral-input"
                value={form.referral_code}
                onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none uppercase tracking-widest"
                placeholder="Get ₹100 bonus"
              />
            </div>
            {err && <div data-testid="register-error" className="text-sm text-[#FF5A36]">{err}</div>}
            <button
              type="submit"
              data-testid="register-submit-btn"
              disabled={busy}
              className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {busy ? "Creating..." : "Create Account"} <ArrowRight size={16} />
            </button>
          </form>

          <div className="text-sm text-[#A0A4AB] text-center mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-[#D4AF37] hover:text-[#E5C865]">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
